import { useState, useCallback } from "react";
import { collection, query, orderBy, getDocs, where } from "firebase/firestore";
import { db } from "../../../firebase";
import { F, D } from "../../../styles/palette";
import { DC, Bdg } from "../../shared";
import { computeHash } from "../../../utils/hashIntegrity";

export default function AdminIntegrity({ user }) {
  const [searchTerm, setSearchTerm] = useState("");
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [records, setRecords] = useState([]);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verified, setVerified] = useState(false);

  const searchPatients = useCallback(async () => {
    if (!searchTerm.trim()) return;
    setLoading(true);
    try {
      const t = searchTerm.trim();
      let docs = [];
      if (/^\d+$/.test(t)) {
        const snap = await getDocs(query(collection(db, "patients"), where("numeroDocumento", "==", t)));
        docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      } else {
        const upper = t.charAt(0).toUpperCase() + t.slice(1).toLowerCase();
        const end = upper.slice(0, -1) + String.fromCharCode(upper.charCodeAt(upper.length - 1) + 1);
        const snap = await getDocs(query(collection(db, "patients"), where("primerApellido", ">=", upper), where("primerApellido", "<", end), orderBy("primerApellido")));
        docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      }
      setPatients(docs);
      setSelectedPatient(null);
      setRecords([]);
      setResults([]);
      setVerified(false);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [searchTerm]);

  const selectPatient = async (p) => {
    setSelectedPatient(p);
    setVerified(false);
    setResults([]);
    setLoading(true);
    try {
      const snap = await getDocs(query(collection(db, "patients", p.id, "clinical_records"), orderBy("fecha", "desc")));
      setRecords(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const verifyAll = async () => {
    setVerifying(true);
    const res = [];
    for (const r of records) {
      const data = {
        subjetivo: r.subjetivo,
        objetivo: r.objetivo,
        analisis: r.analisis,
        plan: r.plan,
      };
      const recalculated = await computeHash(JSON.stringify(data));
      const stored = r.hashIntegridad || "";
      res.push({
        id: r.id,
        consecutivo: r.consecutivo,
        fecha: r.fecha?.toDate ? r.fecha.toDate().toLocaleDateString("es-CO") : "",
        storedHash: stored,
        calculatedHash: recalculated,
        intact: recalculated === stored,
      });
    }
    setResults(res);
    setVerified(true);
    setVerifying(false);
  };

  const intactCount = results.filter(r => r.intact).length;
  const alteredCount = results.filter(r => !r.intact).length;

  return (
    <div>
      <DC title="Verificación de integridad SHA-256" accent="#ca8a04">
        <p style={{ color: "#94a3b8", fontSize: 12, margin: "0 0 10px", lineHeight: 1.6 }}>
          Recalcula el hash SHA-256 de cada evolución clínica y compara con el hash almacenado al momento de la firma digital (Ley 2015/2020).
        </p>
      </DC>

      {/* Search patient */}
      <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
        <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
          onKeyDown={e => e.key === "Enter" && searchPatients()}
          placeholder="Buscar paciente por apellido o documento..."
          style={{ flex: 1, padding: "10px 14px", borderRadius: 8, border: `1px solid ${D.bdr}`, background: "rgba(30,41,59,0.8)", color: "#e2e8f0", fontSize: 13, fontFamily: F.b, boxSizing: "border-box" }} />
        <button onClick={searchPatients} disabled={loading}
          style={{ padding: "10px 18px", borderRadius: 8, border: "none", background: D.ac, color: "#020617", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: F.b }}>
          Buscar
        </button>
      </div>

      {/* Patient list */}
      {patients.length > 0 && !selectedPatient && (
        <div style={{ marginBottom: 14 }}>
          {patients.map(p => (
            <div key={p.id} onClick={() => selectPatient(p)}
              style={{ background: D.card, border: `1px solid ${D.bdr}`, borderRadius: 10, padding: "10px 14px", marginBottom: 4, cursor: "pointer" }}
              onMouseEnter={e => e.currentTarget.style.borderColor = "rgba(45,212,191,0.4)"}
              onMouseLeave={e => e.currentTarget.style.borderColor = D.bdr}>
              <span style={{ color: "#e2e8f0", fontSize: 13, fontWeight: 600 }}>{p.primerApellido} {p.segundoApellido || ""}, {p.primerNombre}</span>
              <span style={{ color: "#64748b", fontSize: 12, marginLeft: 10 }}>{p.tipoDocumento}: {p.numeroDocumento}</span>
            </div>
          ))}
        </div>
      )}

      {/* Selected patient */}
      {selectedPatient && (
        <div>
          <button onClick={() => { setSelectedPatient(null); setRecords([]); setResults([]); setVerified(false); }}
            style={{ background: "none", border: "none", color: D.ac, cursor: "pointer", fontSize: 12, fontFamily: F.b, marginBottom: 10, padding: 0 }}>
            &larr; Cambiar paciente
          </button>

          <DC accent="#0284c7">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <span style={{ color: "#e2e8f0", fontSize: 14, fontWeight: 700 }}>
                  {selectedPatient.primerNombre} {selectedPatient.primerApellido}
                </span>
                <span style={{ color: "#64748b", fontSize: 12, marginLeft: 8 }}>{records.length} evoluciones</span>
              </div>
              <button onClick={verifyAll} disabled={verifying || records.length === 0}
                style={{ padding: "8px 18px", borderRadius: 8, border: "none", background: verifying ? "#475569" : "#ca8a04", color: "#fff", fontSize: 12, fontWeight: 700, cursor: verifying ? "default" : "pointer", fontFamily: F.b }}>
                {verifying ? "Verificando..." : "Verificar integridad"}
              </button>
            </div>
          </DC>

          {/* Results summary */}
          {verified && (
            <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
              <div style={{ flex: 1, textAlign: "center", padding: 14, background: "rgba(22,163,74,0.08)", border: "1px solid rgba(22,163,74,0.3)", borderRadius: 10 }}>
                <div style={{ fontSize: 24, fontWeight: 800, color: "#4ade80", fontFamily: F.m }}>{intactCount}</div>
                <div style={{ fontSize: 11, color: "#16a34a" }}>Íntegros</div>
              </div>
              <div style={{ flex: 1, textAlign: "center", padding: 14, background: alteredCount > 0 ? "rgba(220,38,38,0.08)" : "rgba(30,41,59,0.5)", border: `1px solid ${alteredCount > 0 ? "rgba(220,38,38,0.3)" : D.bdr}`, borderRadius: 10 }}>
                <div style={{ fontSize: 24, fontWeight: 800, color: alteredCount > 0 ? "#f87171" : "#64748b", fontFamily: F.m }}>{alteredCount}</div>
                <div style={{ fontSize: 11, color: alteredCount > 0 ? "#dc2626" : "#64748b" }}>Alterados</div>
              </div>
            </div>
          )}

          {/* Record-by-record results */}
          {results.map(r => (
            <div key={r.id}
              style={{ background: D.card, border: `1px solid ${r.intact ? "rgba(22,163,74,0.2)" : "rgba(220,38,38,0.4)"}`, borderRadius: 10, padding: "10px 14px", marginBottom: 4, backdropFilter: "blur(12px)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <span style={{ color: D.ac, fontSize: 12, fontWeight: 800, fontFamily: F.m, marginRight: 6 }}>#{r.consecutivo}</span>
                  <span style={{ color: "#e2e8f0", fontSize: 12 }}>{r.fecha}</span>
                </div>
                <Bdg t={r.intact ? "Íntegro" : "ALTERADO"} c={r.intact ? "#16a34a" : "#dc2626"} />
              </div>
              <div style={{ marginTop: 6, fontSize: 9, fontFamily: F.m, color: "#475569" }}>
                <div>Almacenado: {r.storedHash ? r.storedHash.slice(0, 32) + "..." : "Sin hash"}</div>
                <div>Calculado:&nbsp; {r.calculatedHash.slice(0, 32)}...</div>
              </div>
            </div>
          ))}

          {/* Records without verification yet */}
          {!verified && records.length > 0 && (
            <p style={{ color: "#64748b", fontSize: 12, textAlign: "center", marginTop: 10 }}>
              Haga clic en "Verificar integridad" para recalcular los hashes SHA-256.
            </p>
          )}

          {records.length === 0 && !loading && (
            <p style={{ color: D.txM, fontSize: 13, textAlign: "center", padding: 20 }}>Este paciente no tiene evoluciones registradas.</p>
          )}
        </div>
      )}
    </div>
  );
}
